import {useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {TSector} from '../../types/sector.type'
import {useTranslation} from '../../utils/useTranslation'
import {pruneIds} from '../../utils/filters.functions'
import {useListSectorsByNetwork} from '../../hooks/sector.hooks'
import {useSelectedSectorIds, useUIActions} from '../../store'
import {useFilterTracking} from '../../hooks/filterTracking.hooks'
import FilterPopover from '../FilterPopover'

/**
 * Sector filter for the network-wide list views (activities, notices).
 * Self-contained: reads the
 * network id straight from the route (the URL is network-scoped since Lot 3),
 * fetches every sector of that network, reads/writes `selectedSectorIds` from
 * the UI store, and tracks its own usage. Self-heal pruning uses the raw store
 * setter so cleanup never emits a filter event.
 */
export default function SectorFilter() {
  const {t} = useTranslation()
  const {networkId} = useParams()
  const {data} = useListSectorsByNetwork(networkId)
  const sectors: TSector[] = useMemo(() => data ?? [], [data])

  const selectedSectorIds = useSelectedSectorIds()
  const {setSelectedSectorIds} = useUIActions()
  const trackFilter = useFilterTracking()

  // Self-heal: drop persisted sector ids absent from this network's catalogue
  // (after a network switch or rehydration). Skip while the list is empty — it
  // is also empty during loading, and we must not wipe a valid selection before
  // the sectors have arrived.
  useEffect(() => {
    if (sectors.length === 0) return
    const pruned = pruneIds(
      selectedSectorIds,
      sectors.map(s => s.id),
    )
    if (pruned !== selectedSectorIds) setSelectedSectorIds(pruned)
  }, [sectors, selectedSectorIds, setSelectedSectorIds])

  const handleToggle = (id: string) => {
    const next = selectedSectorIds.includes(id)
      ? selectedSectorIds.filter(sid => sid !== id)
      : [...selectedSectorIds, id]
    setSelectedSectorIds(next)
    trackFilter('sector')
  }

  const items = sectors.map(s => ({
    id: s.id,
    label: s.name || s.acronym,
  }))

  return (
    <FilterPopover
      items={items}
      selectedIds={selectedSectorIds}
      onToggle={handleToggle}
      buttonIcon='sectors'
      buttonLabel={t('sector:filterLabel')}
    />
  )
}
